import { Builtins, Cli } from "clipanion";
import { installFoundryDnsShim } from "./lib/dns-shim.js";
import { CLI_VERSION } from "./lib/package-version.js";

// Installed before any command runs: on macOS the stub resolver cannot follow the multi-hop
// CNAME behind *.services.ai.azure.com, so every Foundry data-plane call fails ENOTFOUND
// without it. Idempotent, and a no-op for every other hostname.
installFoundryDnsShim();
import { renderError } from "./lib/render-error.js";
import { BindAddCommand } from "./commands/bind/add.js";
import { BindCleanupCommand } from "./commands/bind/cleanup.js";
import { BindListCommand } from "./commands/bind/list.js";
import { BindRemoveCommand } from "./commands/bind/remove.js";
import { BindShowCommand } from "./commands/bind/show.js";
import { ConfigResetCommand } from "./commands/config/reset.js";
import { ConfigSetCommand } from "./commands/config/set.js";
import { ConfigShowCommand } from "./commands/config/show.js";
import { TeamAddCommand } from "./commands/team/add.js";
import { TeamAddIdentityCommand } from "./commands/team/add-identity.js";
import { TeamListCommand } from "./commands/team/list.js";
import { TeamRemoveCommand } from "./commands/team/remove.js";
import { TeamRemoveIdentityCommand } from "./commands/team/remove-identity.js";
import { TeamShowCommand } from "./commands/team/show.js";
import { BrainAppCreateCommand } from "./commands/brain/app-create.js";
import { BrainCheckAppCommand } from "./commands/brain/check-app.js";
import { BrainCreateCommand } from "./commands/brain/create.js";
import { BrainLinkCommand } from "./commands/brain/link.js";
import { BrainListCommand } from "./commands/brain/list.js";
import { BrainOrgsCommand } from "./commands/brain/orgs.js";
import { BrainShowCommand } from "./commands/brain/show.js";
import { BrainUnlinkCommand } from "./commands/brain/unlink.js";
import { AgentDeployAdvisorCommand } from "./commands/agent/deploy-advisor.js";
import { AgentRemoveCommand } from "./commands/agent/remove.js";
import { A2aEnableCommand } from "./commands/a2a/enable.js";
import { A2aDisableCommand } from "./commands/a2a/disable.js";
import { ArchitectCheckCommand } from "./commands/architect/check.js";
import { AgentContentSyncCommand } from "./commands/agent-content/sync.js";
import { AgentContentPinCommand } from "./commands/agent-content/pin.js";
import { CoderDeployCommand } from "./commands/coder/deploy.js";
import { CoderTeardownCommand } from "./commands/coder/teardown.js";
import { AzureExecDeployCommand } from "./commands/azure-exec/deploy.js";
import { PlatformStatusCommand } from "./commands/platform/status.js";
import { PlatformUpdateCommand } from "./commands/platform/update.js";
import { PlatformConvergeCommand } from "./commands/platform/converge.js";
import { PlatformClearIntentCommand } from "./commands/platform/clear-intent.js";
import { PlatformRequestUpdateCommand } from "./commands/platform/request-update.js";
import { PlatformSeedStampCommand } from "./commands/platform/seed-stamp.js";
import { PlatformGatewayAdoptCommand } from "./commands/platform/gateway-adopt.js";
import { PlatformPolicyCommand } from "./commands/platform/policy.js";
import { PlatformEnableCostReportCommand } from "./commands/platform/enable-cost-report.js";
import { PlatformEmailCommand } from "./commands/platform/email.js";
import { PlatformEnableAutoUpdateCommand } from "./commands/platform/enable-auto-update.js";
import { DeployCommand } from "./commands/deploy.js";
import { EvalSkillCommand } from "./commands/eval/skill.js";
import { EvalExamCommand } from "./commands/eval/exam.js";
import { VersionCommand } from "./commands/version.js";
import { WhoamiCommand } from "./commands/whoami.js";
import { StatusCommand } from "./commands/status.js";
import { DoctorCommand } from "./commands/doctor.js";
import { PrereqsCommand } from "./commands/prereqs.js";
import { SwitchCommand } from "./commands/switch.js";
import { OpenCommand } from "./commands/open.js";
import { DreamRunCommand } from "./commands/dream/run.js";
import { ConversationsSweepCommand } from "./commands/conversations/sweep.js";
import { FoundryCreateCommand } from "./commands/foundry/create.js";
import { FoundryAwaitReadyCommand } from "./commands/foundry/await-ready.js";
import { BootstrapPreflightCommand } from "./commands/bootstrap/preflight.js";
import { BootstrapLaunchCommand } from "./commands/bootstrap/launch.js";
import { BootstrapStatusCommand } from "./commands/bootstrap/status.js";
import { BootstrapReapCommand } from "./commands/bootstrap/reap.js";
import { BootstrapUiCommand } from "./commands/bootstrap/ui.js";
import { BootstrapProfileCommand } from "./commands/bootstrap/profile.js";
import { BootstrapSeedProfileCommand } from "./commands/bootstrap/seed-profile.js";
import { TelemetryEnrollCommand } from "./commands/telemetry/enroll.js";
import { CompanionBridgeCommand } from "./commands/companion/bridge.js";
import { CompanionInstallCommand } from "./commands/companion/install.js";
import { CompanionStatusCommand } from "./commands/companion/status.js";
import { CompanionRepairCommand } from "./commands/companion/repair.js";
import { CompanionUninstallCommand } from "./commands/companion/uninstall.js";

// REGISTRY — every file under src/commands/ MUST appear here.
// commands.registry.test.ts walks src/commands/ and asserts each is registered.
const cli = new Cli({
  binaryName: "m8t",
  binaryLabel: "m8t CLI — manage m8t deployments",
  binaryVersion: CLI_VERSION,
});

cli.register(Builtins.HelpCommand);
cli.register(VersionCommand);
cli.register(WhoamiCommand);
cli.register(StatusCommand);
cli.register(DoctorCommand);
cli.register(PrereqsCommand);
cli.register(SwitchCommand);
cli.register(OpenCommand);
cli.register(ConfigShowCommand);
cli.register(ConfigResetCommand);
cli.register(ConfigSetCommand);
cli.register(BindAddCommand);
cli.register(BindCleanupCommand);
cli.register(BindListCommand);
cli.register(BindRemoveCommand);
cli.register(BindShowCommand);
cli.register(TeamAddCommand);
cli.register(TeamAddIdentityCommand);
cli.register(TeamListCommand);
cli.register(TeamRemoveCommand);
cli.register(TeamRemoveIdentityCommand);
cli.register(TeamShowCommand);
cli.register(BrainAppCreateCommand);
cli.register(BrainCheckAppCommand);
cli.register(BrainCreateCommand);
cli.register(BrainLinkCommand);
cli.register(BrainListCommand);
cli.register(BrainOrgsCommand);
cli.register(BrainShowCommand);
cli.register(BrainUnlinkCommand);
cli.register(ArchitectCheckCommand);
cli.register(AgentContentSyncCommand);
cli.register(AgentContentPinCommand);
cli.register(CoderDeployCommand);
cli.register(CoderTeardownCommand);
cli.register(AzureExecDeployCommand);
cli.register(PlatformStatusCommand);
cli.register(PlatformUpdateCommand);
cli.register(PlatformConvergeCommand);
cli.register(PlatformClearIntentCommand);
cli.register(PlatformRequestUpdateCommand);
cli.register(PlatformSeedStampCommand);
cli.register(PlatformGatewayAdoptCommand);
cli.register(PlatformPolicyCommand);
cli.register(PlatformEnableCostReportCommand);
cli.register(PlatformEmailCommand);
cli.register(PlatformEnableAutoUpdateCommand);
cli.register(DeployCommand);
cli.register(AgentDeployAdvisorCommand);
cli.register(AgentRemoveCommand);
cli.register(A2aEnableCommand);
cli.register(A2aDisableCommand);
cli.register(EvalSkillCommand);
cli.register(EvalExamCommand);
cli.register(DreamRunCommand);
cli.register(ConversationsSweepCommand);
cli.register(FoundryCreateCommand);
cli.register(FoundryAwaitReadyCommand);
cli.register(BootstrapPreflightCommand);
cli.register(BootstrapLaunchCommand);
cli.register(BootstrapStatusCommand);
cli.register(BootstrapReapCommand);
cli.register(BootstrapUiCommand);
cli.register(BootstrapProfileCommand);
cli.register(BootstrapSeedProfileCommand);
cli.register(TelemetryEnrollCommand);
cli.register(CompanionBridgeCommand);
cli.register(CompanionInstallCommand);
cli.register(CompanionStatusCommand);
cli.register(CompanionRepairCommand);
cli.register(CompanionUninstallCommand);

async function main(): Promise<number> {
  try {
    return await cli.run(process.argv.slice(2));
  } catch (e) {
    const verbose = process.argv.includes("--verbose");
    const outputIdx = process.argv.indexOf("--output");
    const output =
      outputIdx >= 0 && outputIdx + 1 < process.argv.length
        ? (process.argv[outputIdx + 1] as "pretty" | "json" | "auto")
        : undefined;
    return renderError(e, { stderr: process.stderr, verbose, output });
  }
}

void main().then((code) => { process.exit(code); });
